// ModernToolPropertiesDetailsPreference — Modern ツールプロパティの「詳細」disclosure
// (手ぶれ補正 / アンチエイリアス / スポイトの取得元) の開閉状態を per-user で
// localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景 (docs/_wip/modern-brush-panel-2026-08-17.md §5.2):
//   - エアブラシ追加でツールプロパティが縦に溢れた問題の一部として、低頻度な 3 行
//     (手ぶれ補正 / AA / スポイトの取得元) を **既定 collapsed の折りたたみ**に入れる。
//   - 畳んでよい根拠: 3 つとも `PenSettingsModal` と同一 source を共有済みで、別の
//     到達経路が既にある (= 発見性の損失がほぼない、§5.2)。
//   - 開閉状態は localStorage に永続 (既存の設定永続の流儀、`stabilizerPreference.ts`
//     と同流儀)。
//
// `ModernToolProperties` 自体は「call-directly + tree-walk」で単体テストされている
// 既存の hook レス関数コンポーネント (modernToolProperties*.test.tsx が
// `ModernToolProperties(props)` を直接呼んで戻り値の tree を検証する。この様式は
// React のレンダーパスの外で呼ばれるため、component 内で hook を使うと
// 「Invalid hook call」になる)。そのため開閉 state は **`ModernLayout` 側で保持し
// props (`detailsOpen` / `onDetailsToggle`) として渡す** (= `chatFontScale` /
// `sidebarScale` と同じ「ローカル UI state は ModernLayout が保持」パターン)。
// このファイルは ModernLayout から呼ばれる永続化ストアだけを提供する。
//
// Tests: modernToolPropertiesDetailsPreference.test.ts (storage-injection style)。

/** 既定値 (= 畳んだ状態、ユーザー確定)。 */
export const DEFAULT_TOOL_PROPERTIES_DETAILS_OPEN = false;

/** localStorage キー: ツールプロパティ「詳細」disclosure の開閉状態. */
export const TOOL_PROPERTIES_DETAILS_OPEN_KEY = 'oekaki:modern-tool-properties-details-open';

/**
 * Minimal Storage shape this module uses (stabilizerPreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ModernToolPropertiesDetailsPreferenceStore {
  /** 現在の開閉状態を読み出し. 不在 / 不正 / storage 不可 → `DEFAULT_TOOL_PROPERTIES_DETAILS_OPEN`. */
  get(): boolean;
  /** 開閉状態を保存. storage 不可なら no-op (例外を throw しない). */
  set(open: boolean): void;
  /** key を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * stabilizerPreference.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:modern-tool-properties-details-open__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateModernToolPropertiesDetailsPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns DEFAULT_TOOL_PROPERTIES_DETAILS_OPEN, setter
   * silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readDetailsOpen = (storage: StorageLike): boolean => {
  try {
    const raw = storage.getItem(TOOL_PROPERTIES_DETAILS_OPEN_KEY);
    if (raw === 'open') return true;
    if (raw === 'closed') return false;
    return DEFAULT_TOOL_PROPERTIES_DETAILS_OPEN;
  } catch {
    return DEFAULT_TOOL_PROPERTIES_DETAILS_OPEN;
  }
};

const writeDetailsOpen = (storage: StorageLike, open: boolean): void => {
  try {
    storage.setItem(TOOL_PROPERTIES_DETAILS_OPEN_KEY, open ? 'open' : 'closed');
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `ModernToolPropertiesDetailsPreferenceStore` のインスタンスを構築. storage が
 * unavailable な環境 (SSR / Safari private browsing 厳格モード) でも getter は
 * default (畳んだ状態) を返すだけで、setter は no-op (= 例外で UI が壊れることはない).
 */
export const createModernToolPropertiesDetailsPreferenceStore = (
  opts: CreateModernToolPropertiesDetailsPreferenceStoreOptions = {},
): ModernToolPropertiesDetailsPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_TOOL_PROPERTIES_DETAILS_OPEN;
      return readDetailsOpen(storage);
    },
    set(open) {
      if (storage === null) return;
      writeDetailsOpen(storage, open);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(TOOL_PROPERTIES_DETAILS_OPEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
